// /app-demo — Fase 2 interactieve consumenten-demo (web-port 1-op-1 van app-demo.tsx)
// Flow: start → match → iceberg → woonreis → wooncoach → partners → end
// Fictieve data — 0 impact op live users/CRM/notificaties.
import React, { useState, useEffect } from 'react';
import Icon from './Icon';
import { IcebergExperience } from './IcebergExperience';
import { useDemoTracking } from './useDemoTracking';

const NAVY = '#0e2340';
const NAVY_DEEP = '#050a14';
const GOLD = '#c48a3a';
const INK = '#0e2340';
const INK_MUTED = '#5a6b7d';

const STEP_ORDER = ['start', 'match', 'iceberg', 'woonreis', 'wooncoach', 'partners', 'end'];

export default function AppDemo() {
  const [mode, setMode] = useState(null);
  const [step, setStep] = useState('start');
  const [savedProperty, setSavedProperty] = useState(false);
  const [swipedNo, setSwipedNo] = useState(false);
  const { track } = useDemoTracking('app-demo');

  useEffect(() => { track('demo_started', { module: 'app-demo' }); }, []); // eslint-disable-line

  function goto(next) {
    setStep(next);
    track(`${next}_open`, { mode, previous: step, saved: savedProperty, swiped_no: swipedNo });
  }

  function chooseMode(m) {
    setMode(m);
    track(m === 'solo' ? 'solo_selected' : 'couple_selected');
    goto('match');
  }

  const stepIdx = STEP_ORDER.indexOf(step);

  return (
    <div style={st.safe} data-testid="app-demo-route">
      <div style={st.root}>
        <div style={st.appFrame}>
          <div style={st.topbar}>
            <div style={st.brand}>
              IMMO<span style={st.brandGold}>match</span> <span style={st.demoPill}>DEMO</span>
            </div>
            {mode ? (
              <button className="immo-btn" data-testid="app-demo-reset" onClick={() => { setMode(null); goto('start'); }}>
                <Icon name="rotate-ccw" size={16} color="rgba(255,255,255,0.7)" />
              </button>
            ) : null}
          </div>

          {mode ? (
            <div style={st.progress}>
              {STEP_ORDER.slice(1).map((s, i) => (
                <div key={s} style={{ ...st.dot, ...(i <= stepIdx - 1 ? st.dotActive : {}) }} />
              ))}
            </div>
          ) : null}

          <div style={st.scroll}>
            {step === 'start' ? <StartScreen onPick={chooseMode} /> : null}
            {step === 'match' ? <MatchScreen mode={mode} saved={savedProperty} setSaved={setSavedProperty} setSwipedNo={setSwipedNo} onNext={() => goto('iceberg')} track={track} /> : null}
            {step === 'iceberg' ? <IcebergWrap saved={savedProperty} onNext={() => goto('woonreis')} /> : null}
            {step === 'woonreis' ? <WoonreisScreen mode={mode} saved={savedProperty} onNext={() => goto('wooncoach')} /> : null}
            {step === 'wooncoach' ? <WooncoachScreen mode={mode} saved={savedProperty} onNext={() => goto('partners')} /> : null}
            {step === 'partners' ? <PartnersScreen mode={mode} saved={savedProperty} onNext={() => goto('end')} track={track} /> : null}
            {step === 'end' ? <EndScreen mode={mode} saved={savedProperty} track={track} /> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── STAP: Start ─────────────────────────────────────────────────
function StartScreen({ onPick }) {
  return (
    <div className="anim-fade-in" style={st.startWrap}>
      <div style={st.startEyebrow}>DEMO · INTERACTIEVE ERVARING</div>
      <div style={st.startTitle}>Hoe wil je zoeken?</div>
      <div style={st.startSub}>Kies om te starten. Alles is een demo — geen registratie nodig.</div>
      <div style={st.modeCards}>
        <button className="immo-btn" style={st.modeCard} onClick={() => onPick('solo')} data-testid="mode-solo">
          <div style={st.modeIcon}><Icon name="user" size={22} color={NAVY} /></div>
          <div style={st.modeTitle}>Alleen</div>
          <div style={st.modeDesc}>Ontdek woningen die bij jou passen. IMMO MATCH leert jouw voorkeuren.</div>
          <div style={st.modeArrow}>Start →</div>
        </button>
        <button className="immo-btn" style={st.modeCard} onClick={() => onPick('couple')} data-testid="mode-couple">
          <div style={{ ...st.modeIcon, background: 'rgba(196,138,58,0.15)' }}><Icon name="users" size={22} color={GOLD} /></div>
          <div style={st.modeTitle}>Samen</div>
          <div style={st.modeDesc}>Ontdek samen wat écht bij jullie past. Alleen match als beiden ja zeggen.</div>
          <div style={st.modeArrow}>Start →</div>
        </button>
      </div>
    </div>
  );
}

// ── STAP: Match (interactieve woningkaart) ──────────────────────
function MatchScreen({ mode, saved, setSaved, setSwipedNo, onNext, track }) {
  const pron = mode === 'couple' ? 'jullie' : 'jou';
  return (
    <div className="anim-fade-down" style={st.matchWrap}>
      <div style={st.stepEyebrow}>MATCH ONTDEKKEN</div>
      <div style={st.stepTitle}>Een woning die bij {pron} past.</div>
      <div style={st.stepSub}>Bewaar of veeg weg — jouw gedrag bepaalt de volgende matches.</div>

      <div style={st.pcard}>
        <img src="https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800&auto=format&fit=crop" alt="Karakterhuis Stationsstraat 14, Gent" style={st.pImg} />
        <div style={st.matchBadge}><span style={st.matchBadgeTxt}>92% match</span></div>
        <div style={st.pBody}>
          <div style={st.pTitle}>Karakterhuis · Stationsstraat 14</div>
          <div style={st.pMeta}>Gent · € 320.000 · 3 slpk · EPC B · tuin</div>
          <div style={st.pWhy}>
            <Icon name="info" size={12} color={GOLD} style={{ marginTop: 2, flexShrink: 0 }} />
            <span style={st.pWhyTxt}>Rustige zijstraat · station 4 min · past bij {mode === 'couple' ? 'jullie beide profielen' : 'jouw voorkeuren'}</span>
          </div>
        </div>
      </div>

      <div style={st.actionRow}>
        <button className="immo-btn" style={{ ...st.actionBtn, ...st.actionNo }} onClick={() => { track('property_swiped', { decision: 'no' }); setSwipedNo(true); onNext(); }} data-testid="action-no">
          <Icon name="x" size={18} color="#fff" />
          <span style={st.actionTxt}>Wegvegen</span>
        </button>
        <button
          className="immo-btn"
          style={{ ...st.actionBtn, ...(saved ? st.actionSavedActive : st.actionSave) }}
          onClick={() => { setSaved(true); track('property_saved', { property_id: 'demo_p1' }); }}
          data-testid="action-save"
        >
          <Icon name={saved ? 'check' : 'heart'} size={18} color="#fff" />
          <span style={st.actionTxt}>{saved ? 'Bewaard' : 'Bewaren'}</span>
        </button>
      </div>

      {saved ? (
        <div className="anim-fade-in" style={st.savedNote}>
          <div style={st.savedNoteTxt}>
            💡 IMMO MATCH observeert dat je deze woning bewaart. In de IJsberg zie je zo wat dit gedrag onthult.
          </div>
          <button className="immo-btn" style={st.nextBtn} onClick={onNext} data-testid="to-iceberg">
            <span style={st.nextBtnTxt}>Bekijk de IJsberg →</span>
          </button>
        </div>
      ) : (
        <button className="immo-btn" style={{ ...st.nextBtn, marginTop: 14 }} onClick={onNext} data-testid="skip-to-iceberg">
          <span style={st.nextBtnTxt}>Verder →</span>
        </button>
      )}
    </div>
  );
}

// ── STAP: IJsberg (hergebruik) ──────────────────────────────────
function IcebergWrap({ saved, onNext }) {
  return (
    <div>
      {saved ? (
        <div style={st.connector}>
          <Icon name="corner-down-right" size={13} color={GOLD} style={{ marginTop: 2, flexShrink: 0 }} />
          <div style={st.connectorTxt}>
            <span style={st.connectorAccent}>Op basis van wat je net deed:</span> je bewaarde Stationsstraat 14. Kijk wat IMMO MATCH uit deze actie leert.
          </div>
        </div>
      ) : null}
      <IcebergExperience onAppCta={onNext} onGhostDemo={onNext} />
      <div style={{ textAlign: 'center' }}>
        <button className="immo-btn" style={{ ...st.nextBtn, marginBottom: 20 }} onClick={onNext} data-testid="ic-to-woonreis">
          <span style={st.nextBtnTxt}>Verder naar je woonreis →</span>
        </button>
      </div>
    </div>
  );
}

// ── STAP: Woonreis ──────────────────────────────────────────────
function WoonreisScreen({ mode, saved, onNext }) {
  const pron = mode === 'couple' ? 'Jullie' : 'Jouw';
  const phases = [
    { icon: 'search', label: 'Ontdekken', done: true, hint: 'Je bekeek meerdere woningen' },
    { icon: 'heart', label: 'Match gevonden', done: true, hint: saved ? 'Stationsstraat 14 bewaard · 92% match' : '92% match gevonden' },
    { icon: 'calendar', label: 'Bezoek plannen', current: true },
    { icon: 'dollar-sign', label: 'Financiering', done: false },
    { icon: 'check-circle', label: 'Beslissen', done: false },
    { icon: 'truck', label: 'Verhuizen', done: false },
  ];
  return (
    <div className="anim-fade-down" style={st.pageWrap}>
      <div style={st.connector}>
        <Icon name="corner-down-right" size={13} color={GOLD} style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={st.connectorTxt}>
          <span style={st.connectorAccent}>Wat begrijpt IMMO MATCH:</span> {saved
            ? 'je hebt een sterke match. Logische volgende stap = een bezoek plannen.'
            : 'je oriëntatie-fase loopt. Ik hou je woonreis mee bij.'}
        </div>
      </div>
      <div style={st.stepEyebrow}>{pron.toUpperCase()} WOONREIS</div>
      <div style={st.stepTitle}>IMMO MATCH beweegt met je mee.</div>
      <div style={st.stepSub}>Elke fase heeft een concrete volgende stap — geen "en nu?"-moment.</div>

      <div style={st.timeline}>
        {phases.map((p, i) => (
          <div key={i} style={st.tlRow}>
            <div style={{ ...st.tlDot, ...(p.done ? st.tlDone : {}), ...(p.current ? st.tlCurrent : {}) }}>
              <Icon name={p.icon} size={12} color={p.done || p.current ? '#fff' : INK_MUTED} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ ...st.tlLabel, ...(p.current ? { color: GOLD, fontWeight: 900 } : {}) }}>{p.label}</div>
              {p.current ? <div style={st.tlHint}>Volgende stap · IMMO MATCH stelt drie momenten voor</div> : null}
              {p.done && p.hint ? <div style={st.tlHintDone}>{p.hint}</div> : null}
            </div>
          </div>
        ))}
      </div>

      <button className="immo-btn" style={st.nextBtn} onClick={onNext} data-testid="woonreis-next">
        <span style={st.nextBtnTxt}>Bekijk je Wooncoach →</span>
      </button>
    </div>
  );
}

// ── STAP: Wooncoach ─────────────────────────────────────────────
function WooncoachScreen({ mode, saved, onNext }) {
  const pron = mode === 'couple' ? 'jullie' : 'je';
  const pron2 = mode === 'couple' ? 'jullie gezamenlijke profiel' : 'je profiel';
  return (
    <div className="anim-fade-down" style={st.pageWrap}>
      <div style={st.connector}>
        <Icon name="corner-down-right" size={13} color={GOLD} style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={st.connectorTxt}>
          <span style={st.connectorAccent}>Op basis van {saved ? 'je bewaarde match + gedrag' : 'je gedrag tot nu toe'}:</span> IMMO MATCH bouwt vandaag deze proactieve boodschap voor {pron}.
        </div>
      </div>
      <div style={st.stepEyebrow}>WOONCOACH</div>
      <div style={st.stepTitle}>Advies dat voortkomt uit wat je doet.</div>
      <div style={st.stepSub}>De Wooncoach herleest {pron} gedrag elke ochtend en formuleert de volgende slimme stap.</div>

      <div style={st.coachCard}>
        <div style={st.coachIcon}><Icon name="compass" size={16} color="#fff" /></div>
        <div style={st.coachHi}>Goedemorgen ☀️</div>
        <div style={st.coachBody}>
          {saved
            ? (mode === 'couple'
                ? 'Jullie hebben Stationsstraat 14 gisteren bewaard — de eerste 92%-match uit jullie gezamenlijke profiel. De prijs is vannacht met €10.000 gedaald.'
                : 'Je bewaarde gisteren Stationsstraat 14 — jouw eerste 92%-match. De prijs is vannacht met €10.000 gedaald.')
            : (mode === 'couple'
                ? 'Jullie hebben gisteren twee woningen opnieuw bekeken. Eén daarvan past opvallend goed bij jullie gezamenlijke profiel.'
                : 'Je hebt gisteren twee woningen opnieuw bekeken. Eén daarvan past opvallend goed bij je profiel.')}
        </div>
        <div style={st.coachAdvice}>
          <div style={st.coachAdviceLabel}>MIJN ADVIES VANDAAG</div>
          <div style={st.coachAdviceBody}>{saved
            ? 'Plan meteen een bezoek. Het aantal bewaarde matches op Stationsstraat 14 groeide met 6 in 24u — de kans op een concurrerend bod stijgt.'
            : `Plan een bezoek aan Stationsstraat 14. De prijs zakte gisteren met €10.000 en past nu binnen ${pron2}.`}</div>
        </div>
      </div>

      <button className="immo-btn" style={st.nextBtn} onClick={onNext} data-testid="wooncoach-next">
        <span style={st.nextBtnTxt}>Ontdek relevante partners →</span>
      </button>
    </div>
  );
}

// ── STAP: Partners ──────────────────────────────────────────────
function PartnersScreen({ mode, saved, onNext, track }) {
  const pron = mode === 'couple' ? 'jullie' : 'je';
  const partners = [
    { phase: 'Financiering', icon: 'dollar-sign', accent: '#3b7d5f', title: 'KBC Wonen', desc: `Simuleer haalbaarheid van Stationsstraat 14 op basis van ${mode === 'couple' ? 'jullie gecombineerd inkomen' : 'je inkomen'}.`, cta: 'Meer info', relevant_now: saved },
    { phase: 'Verzekering', icon: 'shield', accent: '#4a72a8', title: 'AG Insurance', desc: 'Bescherming voor de nieuwe woning — offerte op maat.', cta: 'Bekijk offerte', relevant_now: false },
    { phase: 'Notaris', icon: 'file-text', accent: '#7d6a4a', title: 'Notariaat Gent', desc: 'Papierwerk & aankoopcompromis — dichtstbijzijnde notaris.', cta: 'Contact', relevant_now: false },
    { phase: 'Verhuis', icon: 'truck', accent: '#b57041', title: 'MoverMove', desc: 'Verhuisservice met vast tarief — dubbele bemanning.', cta: 'Bereken', relevant_now: false },
  ];
  return (
    <div className="anim-fade-down" style={st.pageWrap}>
      <div style={st.connector}>
        <Icon name="corner-down-right" size={13} color={GOLD} style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={st.connectorTxt}>
          <span style={st.connectorAccent}>Wat er nu logisch is:</span> {saved
            ? 'Financiering wordt nu relevant omdat je een sterke match hebt gevonden. Verhuis en notaris blijven rustig — die komen later in je woonreis.'
            : 'Zolang je oriënteert, blijven partners rustig op de achtergrond. Ze verschijnen wanneer ze relevant worden voor jouw volgende stap.'}
        </div>
      </div>
      <div style={st.stepEyebrow}>PARTNER-ECOSYSTEEM</div>
      <div style={st.stepTitle}>De juiste partner, op het juiste moment.</div>
      <div style={st.stepSub}>Geen sponsorlijst — enkel wanneer relevant voor {pron} woonreis.</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
        {partners.map((p, i) => (
          <button
            key={i}
            className="immo-btn"
            style={{ ...st.partnerCard, ...(p.relevant_now ? st.partnerCardActive : {}) }}
            onClick={() => track('partner_open', { phase: p.phase, name: p.title, relevant_now: p.relevant_now })}
            data-testid={`partner-${p.phase.toLowerCase()}`}
          >
            <div style={{ ...st.pIcon, background: p.accent + '25', borderColor: p.accent + '80' }}>
              <Icon name={p.icon} size={14} color={p.accent} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={st.partPhase}>{p.phase}</span>
                <span style={{ ...st.partWhen, ...(p.relevant_now ? st.partWhenActive : {}) }}>
                  <span style={{ ...st.partWhenTxt, ...(p.relevant_now ? { color: NAVY } : {}) }}>{p.relevant_now ? 'RELEVANT NU' : 'WANNEER RELEVANT'}</span>
                </span>
              </div>
              <div style={st.partName}>{p.title}</div>
              <div style={st.partDesc}>{p.desc}</div>
            </div>
          </button>
        ))}
      </div>

      <button className="immo-btn" style={st.nextBtn} onClick={onNext} data-testid="partners-next">
        <span style={st.nextBtnTxt}>Ik heb genoeg gezien →</span>
      </button>
    </div>
  );
}

// ── STAP: Einde ─────────────────────────────────────────────────
function EndScreen({ mode, saved, track }) {
  return (
    <div className="anim-fade-in" style={st.endWrap}>
      <div style={st.stepEyebrow}>KLAAR</div>
      <div style={st.endTitle}>{saved
        ? (mode === 'couple' ? 'Van gedeelde swipe tot bevestigd bezoek — één woonreis voor jullie.' : 'Van eerste swipe tot bevestigd bezoek — één woonreis voor jou.')
        : (mode === 'couple' ? 'Zo begeleidt IMMO MATCH jullie van eerste swipe tot in je nieuwe thuis.' : 'Zo begeleidt IMMO MATCH je van eerste swipe tot in je nieuwe thuis.')}</div>
      <div style={st.endSub}>Elke actie bouwt je woonprofiel op. Ontdek de app met echte woningen in jouw regio.</div>

      <button
        className="immo-btn"
        style={st.endPrimary}
        data-testid="end-cta-app"
        onClick={() => {
          track('app_cta_clicked', { from: 'app_demo_end', mode });
          window.location.href = `/go/demo?mode=${mode}&autoplay=1&v=2`;
        }}
      >
        <Icon name="arrow-right-circle" size={17} color="#fff" />
        <span style={st.endPrimaryTxt}>Ontdek de IMMO MATCH-app</span>
      </button>

      <button
        className="immo-btn"
        style={{ padding: '8px 0' }}
        data-testid="end-cta-ghost"
        onClick={() => {
          track('ghost_demo_cta_clicked', { from: 'app_demo_end', mode });
          window.location.href = `/go/demo?mode=${mode}&autoplay=1&v=2`;
        }}
      >
        <span style={st.endTextLink}>
          Liever eerst kijken? <span style={{ color: GOLD, fontWeight: 800 }}>Bekijk de rondleiding →</span>
        </span>
      </button>

      <div style={st.endFooter}>Dit was een demo met fictieve data.</div>
    </div>
  );
}

// ── STYLES (1-op-1 uit app-demo.tsx) ────────────────────────────
const st = {
  safe: { minHeight: '100vh', background: `linear-gradient(180deg, ${NAVY}, ${NAVY_DEEP})` },
  root: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  appFrame: { width: '100%', maxWidth: 430, padding: '0 18px', boxSizing: 'border-box' },

  topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12, paddingBottom: 6 },
  brand: { color: '#fff', fontSize: 16, fontWeight: 900, letterSpacing: 1.1 },
  brandGold: { color: GOLD, fontStyle: 'italic', fontWeight: 400 },
  demoPill: { color: GOLD, fontSize: 9, fontWeight: 900, letterSpacing: 1.3 },

  progress: { display: 'flex', gap: 4, marginTop: 4, marginBottom: 10 },
  dot: { flex: 1, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.12)' },
  dotActive: { background: GOLD },

  scroll: { paddingBottom: 40 },

  connector: {
    display: 'flex', gap: 8, alignItems: 'flex-start',
    background: 'rgba(196,138,58,0.08)',
    borderLeft: `2px solid ${GOLD}`,
    padding: '10px 12px',
    borderRadius: 4,
    marginTop: 6, marginBottom: 4,
  },
  connectorTxt: { flex: 1, color: 'rgba(255,255,255,0.85)', fontSize: 12, lineHeight: '17px' },
  connectorAccent: { color: GOLD, fontWeight: 900 },

  startWrap: { paddingTop: 20, display: 'flex', flexDirection: 'column', gap: 10 },
  startEyebrow: { color: GOLD, fontSize: 10, fontWeight: 900, letterSpacing: 1.4 },
  startTitle: { color: '#fff', fontSize: 28, fontWeight: 900, letterSpacing: -0.5, lineHeight: '34px', marginTop: 4 },
  startSub: { color: 'rgba(255,255,255,0.7)', fontSize: 13.5, lineHeight: '19px' },
  modeCards: { display: 'flex', flexDirection: 'column', gap: 12, marginTop: 18 },
  modeCard: { background: '#fff', borderRadius: 18, padding: 18, display: 'flex', flexDirection: 'column', gap: 6, width: '100%', boxSizing: 'border-box' },
  modeIcon: { width: 44, height: 44, borderRadius: 22, background: 'rgba(14,35,64,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modeTitle: { color: INK, fontSize: 20, fontWeight: 900, marginTop: 6 },
  modeDesc: { color: INK_MUTED, fontSize: 13, lineHeight: '18px' },
  modeArrow: { color: GOLD, fontSize: 13, fontWeight: 900, marginTop: 4 },

  pageWrap: { paddingTop: 6, display: 'flex', flexDirection: 'column', gap: 4 },
  matchWrap: { paddingTop: 6 },
  stepEyebrow: { color: GOLD, fontSize: 10, fontWeight: 900, letterSpacing: 1.4 },
  stepTitle: { color: '#fff', fontSize: 22, fontWeight: 900, letterSpacing: -0.3, marginTop: 4, lineHeight: '27px' },
  stepSub: { color: 'rgba(255,255,255,0.7)', fontSize: 13, marginTop: 3, lineHeight: '18px' },

  pcard: { background: '#fff', borderRadius: 20, marginTop: 14, overflow: 'hidden', position: 'relative' },
  pImg: { width: '100%', height: 200, objectFit: 'cover', display: 'block' },
  matchBadge: { position: 'absolute', top: 12, left: 12, background: GOLD, padding: '5px 10px', borderRadius: 999 },
  matchBadgeTxt: { color: '#fff', fontSize: 12, fontWeight: 900 },
  pBody: { padding: 14, display: 'flex', flexDirection: 'column', gap: 4 },
  pTitle: { color: INK, fontSize: 15, fontWeight: 900 },
  pMeta: { color: INK_MUTED, fontSize: 12.5, lineHeight: '17px' },
  pWhy: { display: 'flex', gap: 6, alignItems: 'flex-start', marginTop: 8, background: 'rgba(196,138,58,0.10)', padding: 8, borderRadius: 10 },
  pWhyTxt: { color: INK, fontSize: 11.5, flex: 1, lineHeight: '15px' },

  actionRow: { display: 'flex', gap: 10, marginTop: 14 },
  actionBtn: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '14px 0', borderRadius: 999 },
  actionNo: { background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.15)' },
  actionSave: { background: GOLD },
  actionSavedActive: { background: '#2f8a5c' },
  actionTxt: { color: '#fff', fontSize: 13.5, fontWeight: 900 },
  savedNote: { marginTop: 14, background: 'rgba(196,138,58,0.12)', border: '1px solid rgba(196,138,58,0.35)', padding: 12, borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 8 },
  savedNoteTxt: { color: '#fff', fontSize: 12.5, lineHeight: '17px' },

  nextBtn: { alignSelf: 'flex-start', background: '#fff', padding: '12px 18px', borderRadius: 999, marginTop: 18, display: 'inline-block' },
  nextBtnTxt: { color: NAVY, fontSize: 13.5, fontWeight: 900 },

  timeline: { marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8, background: 'rgba(255,255,255,0.05)', padding: 14, borderRadius: 16, border: '1px solid rgba(255,255,255,0.10)' },
  tlRow: { display: 'flex', alignItems: 'center', gap: 10 },
  tlDot: { width: 28, height: 28, borderRadius: 14, background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  tlDone: { background: '#2f8a5c' },
  tlCurrent: { background: GOLD },
  tlLabel: { color: '#fff', fontSize: 13.5, fontWeight: 700 },
  tlHint: { color: GOLD, fontSize: 11, marginTop: 2 },
  tlHintDone: { color: 'rgba(255,255,255,0.5)', fontSize: 11, marginTop: 2 },

  coachCard: { marginTop: 16, background: '#fff', borderRadius: 18, padding: 16, display: 'flex', flexDirection: 'column', gap: 8 },
  coachIcon: { width: 32, height: 32, borderRadius: 16, background: NAVY, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  coachHi: { color: INK, fontSize: 15, fontWeight: 900, marginTop: 4 },
  coachBody: { color: INK_MUTED, fontSize: 13, lineHeight: '18px' },
  coachAdvice: { marginTop: 8, background: 'rgba(196,138,58,0.10)', padding: 12, borderRadius: 12 },
  coachAdviceLabel: { color: GOLD, fontSize: 10, fontWeight: 900, letterSpacing: 1.2 },
  coachAdviceBody: { color: INK, fontSize: 12.5, marginTop: 4, lineHeight: '17px' },

  partnerCard: { display: 'flex', gap: 10, background: 'rgba(255,255,255,0.05)', padding: 12, borderRadius: 14, border: '1px solid rgba(255,255,255,0.10)', width: '100%', boxSizing: 'border-box' },
  partnerCardActive: { background: 'rgba(196,138,58,0.10)', borderColor: 'rgba(196,138,58,0.45)' },
  pIcon: { width: 32, height: 32, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid', flexShrink: 0 },
  partPhase: { color: GOLD, fontSize: 11, fontWeight: 900, letterSpacing: 0.8 },
  partWhen: { background: 'rgba(255,255,255,0.08)', padding: '2px 6px', borderRadius: 999 },
  partWhenActive: { background: GOLD },
  partWhenTxt: { color: 'rgba(255,255,255,0.55)', fontSize: 8.5, fontWeight: 900, letterSpacing: 0.8 },
  partName: { color: '#fff', fontSize: 13.5, fontWeight: 900, marginTop: 3 },
  partDesc: { color: 'rgba(255,255,255,0.7)', fontSize: 11.5, marginTop: 2, lineHeight: '15px' },

  endWrap: { paddingTop: 40, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' },
  endTitle: { color: '#fff', fontSize: 24, fontWeight: 900, letterSpacing: -0.3, marginTop: 4, lineHeight: '30px' },
  endSub: { color: 'rgba(255,255,255,0.7)', fontSize: 13, lineHeight: '18px' },
  endPrimary: { display: 'flex', alignItems: 'center', gap: 8, background: GOLD, padding: '15px 22px', borderRadius: 999, marginTop: 18 },
  endPrimaryTxt: { color: '#fff', fontSize: 14.5, fontWeight: 900 },
  endTextLink: { color: 'rgba(255,255,255,0.65)', fontSize: 12.5 },
  endFooter: { color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 12, fontStyle: 'italic' },
};
